import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as companyApi from '../../services/company-api.js';
import type { CompanyKnowledge } from '../../services/company-api.js';

@customElement('cp-company-knowledge-widget')
export class CpCompanyKnowledgeWidget extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) companyId = 0;

  @state() private _entries: CompanyKnowledge[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _showForm = false;
  @state() private _editingId: number | null = null;
  @state() private _formTitle = '';
  @state() private _formContent = '';
  @state() private _saving = false;
  @state() private _expandedId: number | null = null;

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    await this._load();
  }

  private async _load(): Promise<void> {
    if (!this.companyId) return;
    this._loading = true;
    this._error = '';
    try {
      this._entries = await companyApi.getCompanyKnowledge(this.companyId);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private _startAdd(): void {
    this._showForm = true;
    this._editingId = null;
    this._formTitle = '';
    this._formContent = '';
    this._error = '';
  }

  private _startEdit(entry: CompanyKnowledge): void {
    this._showForm = true;
    this._editingId = entry.id;
    this._formTitle = entry.title;
    this._formContent = entry.content;
    this._error = '';
  }

  private _cancelForm(): void {
    this._showForm = false;
    this._editingId = null;
    this._formTitle = '';
    this._formContent = '';
    this._error = '';
  }

  private async _save(): Promise<void> {
    if (!this._formTitle.trim() || !this._formContent.trim()) {
      this._error = 'Titel und Inhalt sind erforderlich';
      return;
    }
    this._saving = true;
    this._error = '';
    try {
      if (this._editingId) {
        await companyApi.updateCompanyKnowledge(this.companyId, this._editingId, {
          title: this._formTitle,
          content: this._formContent,
        });
      } else {
        await companyApi.createCompanyKnowledge(this.companyId, {
          title: this._formTitle,
          content: this._formContent,
        });
      }
      this._cancelForm();
      await this._load();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern';
    } finally {
      this._saving = false;
    }
  }

  private async _delete(entryId: number): Promise<void> {
    this._error = '';
    try {
      await companyApi.deleteCompanyKnowledge(this.companyId, entryId);
      if (this._expandedId === entryId) this._expandedId = null;
      await this._load();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Loeschen';
    }
  }

  private _toggleExpand(id: number): void {
    this._expandedId = this._expandedId === id ? null : id;
  }

  private _truncate(text: string, max = 80): string {
    return text.length > max ? text.slice(0, max) + '...' : text;
  }

  override render() {
    if (this._loading) {
      return html`<p class="cp-knowledge__loading">Laden...</p>`;
    }

    return html`
      <div class="cp-knowledge">
        ${this._error ? html`
          <p class="cp-knowledge__error">${this._error}</p>
        ` : nothing}

        ${this._entries.length === 0 && !this._showForm ? html`
          <p class="cp-knowledge__empty">Kein Firmen-Wissen vorhanden</p>
        ` : nothing}

        <!-- Entry List -->
        ${this._entries.length > 0 ? html`
          <div class="cp-knowledge__list">
            ${this._entries.map((entry) => html`
              <div class="cp-knowledge__item ${this._expandedId === entry.id ? 'cp-knowledge__item--expanded' : ''}">
                <button
                  class="cp-knowledge__item-header"
                  @click=${() => this._toggleExpand(entry.id)}
                >
                  <span class="cp-knowledge__item-chevron">${this._expandedId === entry.id ? '▾' : '▸'}</span>
                  <span class="cp-knowledge__item-title">${entry.title}</span>
                </button>
                ${this._expandedId === entry.id ? html`
                  <div class="cp-knowledge__item-body">
                    <p class="cp-knowledge__item-content">${entry.content}</p>
                    <div class="cp-knowledge__item-actions">
                      <button
                        class="cp-knowledge__action-link"
                        @click=${() => this._startEdit(entry)}
                      >Bearbeiten</button>
                      <span class="cp-knowledge__action-sep">·</span>
                      <button
                        class="cp-knowledge__action-link cp-knowledge__action-link--danger"
                        @click=${() => this._delete(entry.id)}
                      >Loeschen</button>
                    </div>
                  </div>
                ` : html`
                  <p class="cp-knowledge__item-preview">${this._truncate(entry.content)}</p>
                `}
              </div>
            `)}
          </div>
        ` : nothing}

        <!-- Inline Form -->
        ${this._showForm ? html`
          <div class="cp-knowledge__form">
            <input
              type="text"
              class="cp-knowledge__input"
              placeholder="Titel"
              .value=${this._formTitle}
              @input=${(e: Event) => { this._formTitle = (e.target as HTMLInputElement).value; }}
            />
            <textarea
              class="cp-knowledge__textarea"
              placeholder="Inhalt / Wissen..."
              rows="4"
              .value=${this._formContent}
              @input=${(e: Event) => { this._formContent = (e.target as HTMLTextAreaElement).value; }}
            ></textarea>
            <div class="cp-knowledge__form-actions">
              <button
                class="cp-knowledge__btn-save"
                @click=${this._save}
                ?disabled=${this._saving}
              >${this._saving ? 'Speichert...' : this._editingId ? 'Aktualisieren' : 'Speichern'}</button>
              <button
                class="cp-knowledge__btn-cancel"
                @click=${this._cancelForm}
              >Abbrechen</button>
            </div>
          </div>
        ` : html`
          <div class="cp-knowledge__add">
            <button
              class="cp-knowledge__action-link"
              @click=${this._startAdd}
            >+ Hinzufuegen</button>
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-company-knowledge-widget': CpCompanyKnowledgeWidget;
  }
}
